import { Flame, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StreakCalendar } from './StreakCalendar';

export function StreakDisplay() {
  const { user } = useAuth();
  const [streak, setStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [weekActivityDays, setWeekActivityDays] = useState<string[]>([]);
  const [weeklyGoal, setWeeklyGoal] = useState(7);
  const [freezesAvailable, setFreezesAvailable] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    
    loadStreak();
    // Removed: updateStreakOnVisit() - streak should only update on meaningful reading activity

    // Subscribe to real-time streak updates
    const channel = supabase
      .channel('streak-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_streaks',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        if (payload.new) {
          const data = payload.new as any;
          setStreak(data.current_streak);
          setLongestStreak(data.longest_streak);
          setWeekActivityDays(Array.isArray(data.week_activity_days) ? data.week_activity_days as string[] : []);
          setWeeklyGoal(data.weekly_goal_days || 7);
          setFreezesAvailable(data.streak_freezes_available || 0);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const loadStreak = async () => {
    try {
      const { data } = await supabase
        .from('user_streaks')
        .select('current_streak, longest_streak, week_activity_days, weekly_goal_days, streak_freezes_available')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (data) {
        setStreak(data.current_streak);
        setLongestStreak(data.longest_streak);
        setWeekActivityDays(Array.isArray(data.week_activity_days) ? data.week_activity_days as string[] : []);
        setWeeklyGoal(data.weekly_goal_days || 7);
        setFreezesAvailable(data.streak_freezes_available || 0);
      }
    } catch (error) {
      console.error('Error loading streak:', error);
    } finally {
      setLoading(false);
    }
  };

  // Removed: updateStreakOnVisit - streaks now only update on meaningful reading activity
  // (rating books, logging progress, completing focus sessions, marking books as finished)

  if (loading || streak === 0) return null;

  const daysThisWeek = weekActivityDays.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 bg-gradient-to-r from-orange-100 to-orange-50 dark:from-orange-900/20 dark:to-orange-900/10 px-3 py-1.5 rounded-full hover:from-orange-200 hover:to-orange-100 dark:hover:from-orange-900/30 dark:hover:to-orange-900/20 transition-all shadow-sm border border-orange-200/50 dark:border-orange-800/50">
          <Flame className="w-4 h-4 text-orange-500 animate-pulse" />
          <span className="text-sm font-bold text-orange-600 dark:text-orange-400">{streak}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" className="w-80 p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-base">{streak} day streak! 🔥</p>
              <p className="text-xs text-muted-foreground">Longest: {longestStreak} days</p>
            </div>
            {freezesAvailable > 0 && (
              <div className="flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/20 px-2 py-1 rounded-full">
                <Shield className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                <span className="text-blue-600 dark:text-blue-400 font-semibold">{freezesAvailable}</span>
              </div>
            )}
          </div>

          <StreakCalendar 
            weekActivityDays={weekActivityDays}
            weeklyGoal={weeklyGoal}
          />

          <div className="text-xs text-muted-foreground pt-2 border-t">
            <p>Log reading progress, reviews, or focus sessions to keep your streak alive!</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
